import { useCallback } from "react";
import { useHotkeys } from "domains/commons/contexts/HotkeysProvider";
import ModelShowLayout from "domains/models/components/ModelShow/Layout";
import { BLEND_MAX_CONCEPTS } from "domains/models/constants/blend";
import { useCreateBlendContext } from "domains/models/contexts/CreateBlendProvider";
import useRouter from "domains/navigation/hooks/useRouter";
import { tagIdToObject } from "domains/tags/utils/tagIdToLabel";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import { useGetModelsByModelIdQuery } from "infra/api/generated/api";
import _ from "lodash";

import { Box, Center, Spinner, VStack } from "@chakra-ui/react";

import BlendBreadcrumb from "./BlendBreadcrumb";

interface BlendShowConceptProps {
  modelId: string;
  categoryId?: string;
  subCategoryId?: string;
  contentScrollRef: React.RefObject<HTMLDivElement>;
}

export default function BlendShowConcept({
  modelId,
  categoryId,
  subCategoryId,
  contentScrollRef,
}: BlendShowConceptProps) {
  const router = useRouter();
  const { concepts, addConcept } = useCreateBlendContext();
  const { selectedProject } = useTeamContext();

  const { data: modelData, isLoading: isLoadingModelData } =
    useGetModelsByModelIdQuery({
      projectId: selectedProject.id,
      modelId,
    });

  const category = tagIdToObject(categoryId);
  const subCategory = category ? tagIdToObject(subCategoryId) : undefined;
  const model = modelData
    ? { id: modelData.model.id, label: modelData.model.name || "Model" }
    : undefined;

  const isMaximumConceptsReached =
    (concepts?.length ?? 0) >= BLEND_MAX_CONCEPTS;

  const withAddButton =
    !isMaximumConceptsReached &&
    !concepts?.some((model) => model.modelId === modelId);

  // ----------------------------------

  const handleClose = useCallback(() => {
    void router.push({
      pathname: router.pathname,
      query: _.omit(router.query, ["selectedModelId"]),
    });
  }, [router]);

  const handleAddToComposition = useCallback(() => {
    if (modelId) addConcept(modelId);
    handleClose();
  }, [modelId, addConcept, handleClose]);

  // ----------------------------------

  useHotkeys("esc", handleClose);

  // ----------------------------------

  return (
    <VStack align="stretch" w="full" h="full" py={6} spacing={0}>
      <Box px={6}>
        <BlendBreadcrumb
          category={category}
          subCategory={subCategory}
          model={model}
          isLoading={isLoadingModelData || !modelData}
        />
      </Box>

      {isLoadingModelData || !modelData ? (
        <Center flex={1} w="full">
          <Spinner />
        </Center>
      ) : (
        <Box mt={-3} mx={-3}>
          <ModelShowLayout
            onAddToComposition={
              withAddButton ? handleAddToComposition : undefined
            }
            model={modelData.model}
            isOnLoraCatalog
            contentScrollRef={contentScrollRef}
          />
        </Box>
      )}
    </VStack>
  );
}
